"use client";

import type { GamePageTranslations } from "@casino/server/graphql/getCmsGamePagePhrases";
import type { GameBoost } from "@casino/server/graphql/getGameBoost";
import type { HomeBoostGame } from "@casino/server/graphql/getHomeBoostGames";
import translatePhrase from "@casino/ui/Games/translatePhrase";
import replaceVariables from "@workspace/cms-phrases/src/phrase/replaceVariables";
import {
  Button,
  cn,
  PromoBanner,
  TypographyText,
  useMediaQuery,
} from "@workspace/frontend-ui";
import { type FC, useEffect, useState } from "react";
import BoostAvailableGames from "./BoostAvailableGames";
import boostButtonLabel from "./boostButtonLabel";
import getGamePageBanners from "./getGamePageBanners";
import getSecondsUntil from "./getSecondsUntil";
import useBoostOptIn from "./hooks/useBoostOptIn";
import MOBILE_MEDIA_QUERY from "./mobileMediaQuery";

type Props = {
  gameId?: string;
  boost: GameBoost;
  totalBalance: number;
  site: Site;
  isMobile: boolean;
  walletCurrency?: string;
  maxBoostAmountDisplay?: string;
  maxBetAmountDisplay?: string;
  isSingle?: boolean;
  isHomepage?: boolean;
  games?: HomeBoostGame[];
  allowedGames?: string;
  translations?: GamePageTranslations;
};

const BoostPromoBanner: FC<Props> = ({
  gameId,
  boost,
  totalBalance,
  site,
  isMobile,
  walletCurrency,
  maxBoostAmountDisplay,
  maxBetAmountDisplay,
  translations,
  isSingle,
  isHomepage,
  games,
  allowedGames,
}) => {
  const {
    isOptedIn,
    isPending,
    handleOptIn,
    timerEndAt: freshTimerEndAt,
    winAmountDisplay,
    boostAmountDisplay,
  } = useBoostOptIn({
    eventId: boost.eventId,
    gameId,
    currency: walletCurrency,
    isOptedIn: boost.isOptedIn,
  });

  const timerEndAt = freshTimerEndAt ?? boost.timerEndAt;
  const [expired, setExpired] = useState(
    isOptedIn && !!timerEndAt && !getSecondsUntil(timerEndAt),
  );

  useEffect(() => {
    if (!isOptedIn || !timerEndAt) return;

    const seconds = getSecondsUntil(timerEndAt);
    if (!seconds) {
      setExpired(true);
      return;
    }

    const timeout = setTimeout(() => setExpired(true), seconds * 1000);
    return () => clearTimeout(timeout);
  }, [isOptedIn, timerEndAt]);

  const percentage = boost.boostPercentage;
  const title = percentage
    ? `${percentage}${translatePhrase(translations, "game.boost.titlesuffix", "% Boost on every win!")}`
    : translatePhrase(
        translations,
        "game.boost.titledefault",
        "Boost on every win!",
      );
  const boostLabel = translatePhrase(
    translations,
    "gamepage.boost.max_amount_boost",
    "MAX BOOST",
  ).toUpperCase();
  const betLabel = translatePhrase(
    translations,
    "gamepage.boost.max_bet",
    "MAX BET",
  ).toUpperCase();
  const totalWinLabel = translatePhrase(
    translations,
    "game.boost.totalwin",
    "Total Win (Accumulated)",
  );
  const totalBoostLabel = translatePhrase(
    translations,
    "game.boost.totalboost",
    "Total Boost (Accumulated)",
  );

  const description = isHomepage
    ? replaceVariables(
        translatePhrase(
          translations,
          "casino.boosts.max_power_up",
          "Max power-up! Score {value}% more every time you win. It’s fluffin’ awesome!",
        ),
        { value: percentage ?? "" },
      )
    : "";

  const { bg, bgMobile, fg, fgMobile } = getGamePageBanners({
    mode: isHomepage ? "boostHomepage" : "boost",
    site,
  });
  const isMobileViewport = useMediaQuery(MOBILE_MEDIA_QUERY, isMobile);

  if (isHomepage && !games?.length) return null; // Region-blocked: no eligible games to show
  if (expired) return null;

  return (
    <PromoBanner
      className={isHomepage ? "min-h-32" : "min-h-20"}
      data-testid="boost-promo-banner"
      fgCenter={isSingle || isHomepage}
      banner={isMobileViewport ? bgMobile : bg}
      bannerForeground={fg}
      bannerForegroundMobile={fgMobile}
      isHomepage={isHomepage}
      title={title}
      description={description}
      timeRemains={
        isOptedIn
          ? getSecondsUntil(timerEndAt)
          : isHomepage
            ? getSecondsUntil(boost.timerEndAt)
            : undefined
      }
    >
      {isHomepage ? (
        <>
          {fg && isHomepage && (
            <img
              src={fg}
              className={cn(
                "h-full w-auto! max-w-none! max-2xl:absolute max-2xl:top-0 max-2xl:right-0",
                fgMobile && "max-md:hidden",
              )}
            />
          )}
          {fgMobile && isHomepage && (
            <img
              src={fgMobile}
              className="h-full w-auto! max-w-none! max-md:block md:hidden absolute top-0 right-0 z-0"
            />
          )}
          <BoostAvailableGames
            games={games ?? []}
            allowedGames={allowedGames}
            translations={translations}
          />
        </>
      ) : (
        <>
          <div className="w-full md:w-fit flex gap-x-2 pr-2 justify-start">
            {isOptedIn ? (
              <>
                {winAmountDisplay && (
                  <div className="flex flex-col gap-y-1 mt-1">
                    <TypographyText
                      className="opacity-80 text-white!"
                      variant="small"
                    >
                      {totalWinLabel}
                    </TypographyText>
                    <TypographyText className="text-white! mt-0 font-bold">
                      {winAmountDisplay}
                    </TypographyText>
                  </div>
                )}
                {boostAmountDisplay && (
                  <div className="flex flex-col gap-y-1 mt-1">
                    <TypographyText
                      className="opacity-80 text-white!"
                      variant="small"
                    >
                      {totalBoostLabel}
                    </TypographyText>
                    <TypographyText className="text-white! mt-0 font-bold">
                      {boostAmountDisplay}
                    </TypographyText>
                  </div>
                )}
              </>
            ) : (
              <>
                {maxBoostAmountDisplay && (
                  <div className="flex flex-col gap-y-1 mt-1">
                    <TypographyText
                      className="opacity-80 text-white!"
                      variant="small"
                    >
                      {boostLabel}
                    </TypographyText>
                    <TypographyText className="text-white! mt-0 font-bold">
                      {maxBoostAmountDisplay}
                    </TypographyText>
                  </div>
                )}
                {maxBetAmountDisplay && (
                  <div className="flex flex-col gap-y-1 mt-1">
                    <TypographyText
                      className="opacity-80 text-white!"
                      variant="small"
                    >
                      {betLabel}
                    </TypographyText>
                    <TypographyText className="text-white! mt-0 font-bold">
                      {maxBetAmountDisplay}
                    </TypographyText>
                  </div>
                )}
              </>
            )}
          </div>
          {!isOptedIn && !isHomepage && (
            <Button
              data-testid="boost-opt-in"
              disabled={isPending || totalBalance <= 0}
              variant="secondary"
              onClick={handleOptIn}
              className="w-fit max-sm:mt-3 h-10 mt-1"
            >
              {boostButtonLabel({ isOptedIn, isPending, translations })}
            </Button>
          )}
        </>
      )}
    </PromoBanner>
  );
};

export default BoostPromoBanner;
